import React, { useState, useEffect, useCallback } from "react";
import { Controller } from "react-hook-form";
import { useTabContext } from "../context/context";
import { useServiceIDLookup } from "../data/AvailTools";
import { MultiSelect } from "react-multi-select-component";
import { FaXmark } from "react-icons/fa6";
import { all } from "axios";

const AvailabilityRow = ({ item, index, control, setValue, remove, saving }) => {
  const { allowedServices, filteredRoomCare, serviceLookup, updateAvailabilityList, setChangesMade, filteredReimbursement, setErrorMessage, setWarningMessage } = useTabContext();

  const [selectedService, setSelectedService] = useState({});
  const [selectedRoom, setSelectedRoom] = useState(item.room || "");
  const [termID, setTermID] = useState("");
  const [loading, setLoading] = useState(true);
  const [allowedRooms, setAllowedRooms] = useState([]);
  const [displayRooms, setDisplayRooms] = useState(false);
  const [displayGender, setDisplayGender] = useState(false);
  const [displayPayment, setDisplayPayment] = useState(false);
  const [daysAvailable, setDaysAvailable] = useState(null);
  const [dateAvailable, setDateAvailable] = useState(null);
  const [expired, setExpired] = useState(false);
  const [payOptions, setPayOptions] = useState([]);
  const [defaultPayments, setDefaultPayments] = useState([]);

  const serviceIDLookup = useServiceIDLookup(allowedServices);

  useEffect(() => {
    if (allowedServices.length > 0 && item.service) {
      const selectService = serviceIDLookup[item.service] || {};
      setSelectedService(selectService);
      setTermID(selectService.term_id);
      setSelectedRoom(item.room || "");
      handleDefaultPayment(item.reimbursement);
    }
  }, [allowedServices, item.service, serviceIDLookup, filteredReimbursement]);

  useEffect(() => {
    setDisplayRooms(selectedService.display_room_care === 1);
    setDisplayPayment(selectedService.display_payment == 1);
    setDaysAvailable(parseInt(selectedService.days_available));
    setDateAvailable(item.date_available);
  }, [selectedService]);

  // DATE VALIDATION USEEFFECT (unchanged)
  useEffect(() => {
    if (daysAvailable) {
      const today = new Date();
      const todayPST = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
      const availableDate = new Date(dateAvailable);
      const differenceInTime = todayPST.getTime() - availableDate.getTime();
      const differenceInDays = Math.floor(differenceInTime / (1000 * 60 * 60 * 24));

      const dateInput = document.querySelector(`input[name="availability[${index}].date_available"]`);
      if (!dateInput) return;

      if (differenceInDays < -60) {
        setErrorMessage("You can't select a date more than 60 days in the future.");
        setWarningMessage("");
        setExpired(false);
        dateInput.classList.add("border-red-500");
      } else if (differenceInDays > daysAvailable) {
        setWarningMessage("Warning: Some of your availability is not being displayed because it’s outdated. Click ‘Save/Update’ to set all expired dates to today.");
        setExpired(true);
        dateInput.classList.add("border-red-500");
      } else {
        setErrorMessage("");
        setWarningMessage("");
        setExpired(false);
        dateInput.classList.remove("border-red-500");
      }
    }
  }, [daysAvailable, dateAvailable, saving]);

  // This sets dateAvailable to 'today' if user picks a date in the past
  const updateDateAvailable = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDate = new Date(dateAvailable);
    currentDate.setHours(0, 0, 0, 0);
    if (currentDate < today) {
      const todayFormat = today.toISOString().split("T")[0];
      setDateAvailable(todayFormat);
      setValue(`availability[${index}].date_available`, todayFormat);
    }
  }, [index, setValue, dateAvailable]);

  // Room updates
  useEffect(() => {
    if (item.room_care) {
      const foundRoom = serviceLookup.find(tax => tax.taxonomy_id == item.room_care && tax.service_id == selectedService.term_id);
      const showGender = foundRoom && foundRoom.show_gender == 1;
      setDisplayGender(showGender);
      setPaymentField(filteredReimbursement);
    }
  }, [item.room, selectedService, filteredReimbursement]);

  // Update allowed rooms and update reimbursement value in the form
  useEffect(() => {
    setAllowedRooms(filteredRoomCare.filter(service => service.service_id == termID));
    setValue(`availability[${index}].reimbursement`, defaultPayments);
  }, [termID, filteredRoomCare, defaultPayments]);

  useEffect(() => {
    console.log("++++++Default reimbursements updated:", defaultPayments);
  }, [defaultPayments]);

  const handleRemoveRow = () => {
    setErrorMessage("");
    setWarningMessage("");
    remove(index);
  };

  const handleDefaultPayment = payments => {
    console.log("PAYMENTS", payments);
    if (!payments) {
      setDefaultPayments([]);
      return;
    }
    let arrayOfIDs = [];
    if (Array.isArray(payments)) {
      arrayOfIDs = payments;
    } else if (typeof payments === "string") {
      arrayOfIDs = payments.split(",");
    }
    const defaultPay = arrayOfIDs
      .map(paymentID => {
        const match = filteredReimbursement.find(opt => opt.taxonomy_id == paymentID);
        return match ? { value: match.taxonomy_id, label: match.taxonomy_name } : null;
      })
      .filter(Boolean);
    setDefaultPayments(defaultPay);
  };

  useEffect(() => {
    if (selectedService.term_id) {
      setPaymentField(filteredReimbursement);
    }
  }, [selectedService, filteredReimbursement]);

  const handleServiceChange = serviceId => {
    const service = allowedServices.find(service => service.term_id === parseInt(serviceId, 10));
    if (!service) {
      console.error(`Service with term_id ${serviceId} not found`);
      return;
    }
    const serviceName = service.name.toLowerCase();
    const selectService = serviceIDLookup[serviceName] || {};
    setSelectedService(selectService);
    setTermID(selectService.term_id);
    setChangesMade(true);
    setValue(`availability[${index}].term_id`, selectService.term_id);
    setValue(`availability[${index}].service`, service.name.toLowerCase());
    setPaymentField(filteredReimbursement);
  };

  const setPaymentField = payments => {
    const paymentOptions = payments.filter(payment => payment.service_id == selectedService.term_id);
    const paymentOptionsFormat = paymentOptions.map(payment => ({
      value: payment.taxonomy_id,
      label: payment.taxonomy_name
    }));
    setPayOptions(paymentOptionsFormat);
  };

  console.log("DEFAULT PAYMENTS", defaultPayments);

  const handleRoomChange = roomCareId => {
    if (roomCareId) {
      const foundRoom = serviceLookup.find(tax => tax.taxonomy_id == roomCareId && tax.service_id == selectedService.term_id);
      const showGender = foundRoom && foundRoom.show_gender === "1";
      setDisplayGender(showGender);
      setSelectedRoom(roomCareId.toString());
    } else {
      setSelectedRoom("");
    }
    setValue(`availability[${index}].room_care`, roomCareId);
    setChangesMade(true);
  };

  useEffect(() => {
    setLoading(false);
  }, [selectedService]);

  const selectedLabels = defaultPayments.map(item => item.label).join(", ");

  return (
    <div className="border-2 bg-gray-50 md:bg-white mb-4 md:mb-0 rounded p-2 md:p-0 md:border-b border-gray-300 items-center first:border-t md:flex">
      {/* Quantity Input */}
      <div className="p-2 md:w-[75px] md:border-r border-b-2 border-gray-100 shrink-0">
        <div className="md:hidden w-full font-semibold">Quantity</div>
        <Controller
          control={control}
          name={`availability[${index}].quantity`}
          render={({ field }) => (
            <input
              {...field}
              className="w-full text-center rounded-md"
              onChange={e => {
                field.onChange(e);
                setChangesMade(true);
              }}
            />
          )}
        />
      </div>

      {/* Service Selection */}
      <div className="p-2 md:w-[200px] md:border-r border-b-2 border-gray-100 shrink-0">
        <div className="md:hidden w-full font-semibold">Service</div>
        {!loading && (
          <Controller
            control={control}
            name={`availability[${index}].term_id`}
            render={({ field }) => (
              <select
                {...field}
                className="w-full rounded-md"
                onChange={e => {
                  field.onChange(e);
                  handleServiceChange(e.target.value);
                  const selectedService = allowedServices.find(service => service.term_id === parseInt(e.target.value, 10));
                  if (selectedService) {
                    setValue(`availability[${index}].service`, selectedService.name.toLowerCase());
                  }
                }}
              >
                <option value="">Select Service</option>
                {allowedServices.map((service, serviceIndex) => (
                  <option key={serviceIndex} value={service.term_id}>
                    {service.name}
                  </option>
                ))}
              </select>
            )}
          />
        )}
      </div>

      {/* Room/Care Selection - Conditional Rendering */}
      {displayRooms ? (
        <div className="p-2 md:w-[200px] md:border-r border-b-2 border-gray-100 shrink-0">
          <div className="md:hidden w-full font-semibold">Room/Care</div>
          <Controller
            control={control}
            name={`availability[${index}].room_care`}
            render={({ field }) => (
              <select
                {...field}
                className="w-full rounded-md"
                onChange={e => {
                  field.onChange(e);
                  handleRoomChange(e.target.value);
                }}
              >
                <option value="">Select Room/Care</option>
                {allowedRooms.map((option, optionIndex) => (
                  <option key={optionIndex} value={option.taxonomy_id}>
                    {option.taxonomy_name}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
      ) : (
        <div className="p-2 md:w-[200px] shrink-0 md:border-r border-b-2 border-gray-100 text-gray-500 text-sm text-center">Select Different Service</div>
      )}

      {/* Gender Selection - Conditional Rendering */}
      {displayGender ? (
        <div className="p-2 md:w-[150px] md:border-r border-b-2 border-gray-100 shrink-0">
          <div className="md:hidden w-full font-semibold">Gender</div>
          <Controller
            control={control}
            name={`availability[${index}].gender`}
            render={({ field }) => (
              <select {...field} className="w-full rounded-md">
                <option value="">Select Gender</option>
                <option value="all">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            )}
          />
        </div>
      ) : (
        <div className="p-2 shrink-0 md:w-[150px] md:border-r border-b-2 border-gray-100 text-gray-500 text-sm text-center">No Gender Requirements</div>
      )}

      {/* Reimbursement/Payment Selection using react-multi-select */}
      <div className="md:hidden w-full font-semibold">Reimbursements</div>

      {displayPayment ? (
        <div className="p-2 grow md:w-[350px] md:border-r border-b-2 border-gray-100 reimb-options">
          <MultiSelect
            options={payOptions}
            value={defaultPayments}
            hasSelectAll={false}
            disableSearch={true}
            onChange={selected => {
              setDefaultPayments(selected);
              setChangesMade(true);
              setValue(`availability[${index}].reimbursement`, selected);
            }}
            labelledBy="Select Payment Options"
            overrideStrings={{
              allItemsAreSelected: defaultPayments.length === payOptions.length ? selectedLabels : ""
            }}
          />
        </div>
      ) : (
        <div className="p-2 w-full md:w-[350px] md:border-r border-b-2 border-gray-100 text-gray-500 text-sm text-center">No Payment Options</div>
      )}

      {/* Date Available Input */}
      <div className="md:hidden w-full font-semibold">Date Available</div>
      <div className="p-2 shrink-0 md:w-[150px] md:border-r border-b-2 border-gray-100">
        <Controller
          control={control}
          name={`availability[${index}].date_available`}
          render={({ field }) => (
            <input
              type="date"
              {...field}
              className="w-full rounded-md"
              onChange={e => {
                field.onChange(e);
                setChangesMade(true);
                setDateAvailable(e.target.value);
              }}
            />
          )}
        />
        {expired && <div className="text-red-500 text-sm">Expired</div>}
      </div>

      {/* Remove Row Button */}
      <div className="p-2 shrink-0 flex justify-center items-center md:w-[50px]">
        <button type="button" onClick={handleRemoveRow} className="text-red-500 hover:text-red-700 text-xl flex items-center">
          <span className="md:hidden">Delete Listing</span>
          <FaXmark />
        </button>
      </div>
    </div>
  );
};

export default AvailabilityRow;
